import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    eyebrow: 'Start your league',
    title: 'It\'s super easy to create your own league!',
    lead: 'Open the League area and begin from the same home base where every season comes together.',
    takeaway: 'Your new league is only a few steps away.',
    image: '/league-manager-slideshow/assets/slide-01.png',
    alt: 'League Runner home page with the League navigation highlighted',
    source: 'League Runner walkthrough',
  },
  {
    eyebrow: 'Stay in control',
    title: 'You can manage everything here...',
    lead: 'League settings, membership, draft preparation, and season controls all live behind one clear management hub.',
    takeaway: 'One place to keep the league organized from setup through race week.',
    image: '/league-manager-slideshow/assets/slide-02.png',
    alt: 'League page with the Manage League button highlighted',
    source: 'League Runner walkthrough',
  },
  {
    eyebrow: 'Bring the group together',
    title: 'Invite your people...',
    lead: 'Send each manager an invitation directly from the league management screen.',
    takeaway: 'Add the people you want, then build the season together.',
    image: '/league-manager-slideshow/assets/slide-03.png',
    alt: 'League invitation dialog for sending an invitation by email',
    source: 'League Runner walkthrough',
  },
  {
    eyebrow: 'Get draft-ready',
    title: 'And set your draft date!',
    lead: 'Choose the date, give every manager time to prepare, and turn a new league into a season ready to begin.',
    image: '/league-manager-slideshow/assets/slide-04.png',
    alt: 'League management screen with Set Draft Date highlighted',
    source: 'League Runner walkthrough · YouTube',
    cta: {
      href: 'https://www.youtube.com/watch?v=95tRaOG8OOs&t=2s',
      label: 'Watch the video walkthrough',
    },
  },
];

const styles = {
  page: {
    minHeight: '100vh',
    background: '#111',
    color: '#171713',
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  homeLink: {
    position: 'fixed',
    zIndex: 12,
    left: '20px',
    top: '18px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 18px',
    borderRadius: '999px',
    background: 'rgba(17,17,17,.82)',
    color: '#fff',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '.03em',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 12px 28px rgba(0,0,0,.22)',
  },
  slide: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns: '42% 58%',
    background: '#f6f1e4',
  },
  titleSlide: {
    gridTemplateColumns: '47% 53%',
    background: '#171713',
    color: '#fff',
  },
  copy: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: 'clamp(48px, 7vw, 120px)',
    paddingRight: 'clamp(36px, 5vw, 84px)',
    position: 'relative',
    zIndex: 1,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '22px',
    fontSize: '12px',
    fontWeight: 900,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
  },
  mark: {
    color: '#d7aa36',
    fontSize: '22px',
    letterSpacing: '-.2em',
  },
  eyebrow: {
    margin: '0 0 22px',
    color: '#956b07',
    fontSize: 'clamp(13px, 1.2vw, 20px)',
    fontWeight: 800,
    letterSpacing: '.17em',
    textTransform: 'uppercase',
  },
  title: {
    margin: 0,
    maxWidth: '12ch',
    lineHeight: '.96',
    letterSpacing: '-.055em',
    fontSize: 'clamp(43px, 5.9vw, 92px)',
  },
  titleHero: {
    fontSize: 'clamp(52px, 7.6vw, 128px)',
  },
  lead: {
    maxWidth: '34ch',
    margin: '30px 0 0',
    fontSize: 'clamp(18px, 1.65vw, 28px)',
    lineHeight: 1.45,
    color: '#454139',
  },
  leadHero: {
    color: '#d8d3c8',
    maxWidth: '29ch',
  },
  takeaway: {
    marginTop: '34px',
    paddingLeft: '20px',
    borderLeft: '5px solid #f6a314',
    maxWidth: '32ch',
    fontSize: 'clamp(16px, 1.35vw, 23px)',
    fontWeight: 650,
    lineHeight: 1.4,
  },
  source: {
    marginTop: '24px',
    color: '#777267',
    fontSize: '11px',
    letterSpacing: '.06em',
  },
  visual: {
    position: 'relative',
    overflow: 'hidden',
    background: '#ddd',
    minHeight: '35vh',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  },
  step: {
    position: 'absolute',
    right: '26px',
    top: '24px',
    zIndex: 2,
    minWidth: '46px',
    height: '46px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#f6a314',
    color: '#171713',
    fontWeight: 900,
    boxShadow: '0 10px 24px rgba(0,0,0,.17)',
  },
  controls: {
    position: 'fixed',
    zIndex: 10,
    left: '50%',
    bottom: '18px',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
    padding: '7px',
    borderRadius: '999px',
    background: 'rgba(17,17,17,.78)',
    backdropFilter: 'blur(10px)',
  },
  controlButton: {
    width: '42px',
    height: '36px',
    border: 0,
    borderRadius: '999px',
    background: 'transparent',
    color: 'white',
    cursor: 'pointer',
  },
  counter: {
    minWidth: '62px',
    color: 'white',
    display: 'grid',
    placeItems: 'center',
    fontSize: '13px',
  },
  progress: {
    position: 'fixed',
    zIndex: 11,
    left: 0,
    right: 0,
    bottom: 0,
    height: '5px',
    background: 'rgba(255,255,255,.16)',
  },
  progressBar: {
    height: '100%',
    background: '#f6a314',
    transition: 'width .35s ease',
  },
  hint: {
    position: 'fixed',
    zIndex: 9,
    top: '18px',
    right: '22px',
    color: 'rgba(255,255,255,.58)',
    fontSize: '12px',
  },
  watch: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    width: 'max-content',
    marginTop: '34px',
    padding: '15px 22px',
    borderRadius: '999px',
    background: '#f6a314',
    color: '#171713',
    textDecoration: 'none',
    fontSize: 'clamp(16px, 1.25vw, 21px)',
    fontWeight: 850,
    boxShadow: '0 12px 30px rgba(160,97,0,.2)',
  },
  watchIcon: {
    width: '27px',
    height: '27px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#171713',
    color: '#fff',
    fontSize: '12px',
  },
};

const LeagueManagerSlideshowScreen = () => {
  const [index, setIndex] = useState(0);
  const slide = slides[index];

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (['ArrowRight', 'PageDown', ' ', 'Enter'].includes(event.key)) {
        event.preventDefault();
        setIndex((current) => (current + 1) % slides.length);
      }

      if (['ArrowLeft', 'PageUp', 'Backspace'].includes(event.key)) {
        event.preventDefault();
        setIndex((current) => (current - 1 + slides.length) % slides.length);
      }

      if (event.key === 'Home') {
        event.preventDefault();
        setIndex(0);
      }

      if (event.key === 'End') {
        event.preventDefault();
        setIndex(slides.length - 1);
      }

      if (event.key.toLowerCase() === 'f') {
        document.documentElement.requestFullscreen?.();
      }
    };

    let touchStart = null;
    const handleTouchStart = (event) => {
      touchStart = event.changedTouches[0].clientX;
    };
    const handleTouchEnd = (event) => {
      if (touchStart === null) {
        return;
      }

      const delta = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(delta) > 55) {
        setIndex((current) => (current + (delta < 0 ? 1 : -1) + slides.length) % slides.length);
      }
      touchStart = null;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    document.title = `${index + 1}/${slides.length} - Create a League`;
    return () => {
      document.title = 'League Runner';
    };
  }, [index]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 820;
  const slideStyle = { ...styles.slide, ...(isMobile ? { gridTemplateColumns: '1fr', gridTemplateRows: '49% 51%' } : null) };
  const copyStyle = isMobile
    ? { ...styles.copy, order: 2, padding: '40px 36px 58px', justifyContent: 'flex-start' }
    : styles.copy;
  const visualStyle = isMobile ? { ...styles.visual, order: 1 } : styles.visual;

  return (
    <div style={styles.page}>
      <Link to="/" style={isMobile ? { ...styles.homeLink, left: '14px', top: '14px', padding: '10px 14px', fontSize: '12px' } : styles.homeLink}>
        ← Return to Home Page
      </Link>

      <div style={isMobile ? { ...styles.hint, display: 'none' } : styles.hint}>← → navigate · F fullscreen</div>

      <main aria-live="polite" style={slideStyle}>
        <div style={copyStyle}>
          <div style={styles.brand}><span style={styles.mark}>◀◀</span> League Runner</div>
          <p style={styles.eyebrow}>{slide.eyebrow}</p>
          <h1 style={styles.title}>{slide.title}</h1>
          <p style={styles.lead}>{slide.lead}</p>
          {slide.takeaway ? <p style={styles.takeaway}>{slide.takeaway}</p> : null}
          {slide.cta ? (
            <a href={slide.cta.href} target="_blank" rel="noopener noreferrer" style={styles.watch}>
              <span style={styles.watchIcon}>▶</span>
              {slide.cta.label}
            </a>
          ) : null}
          <div style={styles.source}>{slide.source}</div>
        </div>
        <div style={visualStyle}>
          <span style={styles.step}>{index + 1}</span>
          <img src={slide.image} alt={slide.alt} style={styles.image} />
        </div>
      </main>

      <nav aria-label="Slideshow controls" style={styles.controls}>
        <button type="button" aria-label="Previous slide" style={styles.controlButton} onClick={() => setIndex((current) => (current - 1 + slides.length) % slides.length)}>←</button>
        <div style={styles.counter}><span>{index + 1}</span>&nbsp;/&nbsp;<span>{slides.length}</span></div>
        <button type="button" aria-label="Next slide" style={styles.controlButton} onClick={() => setIndex((current) => (current + 1) % slides.length)}>→</button>
      </nav>
      <div aria-hidden="true" style={styles.progress}>
        <div style={{ ...styles.progressBar, width: `${((index + 1) / slides.length) * 100}%` }} />
      </div>
    </div>
  );
};

export default LeagueManagerSlideshowScreen;
